import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { BaseChapterProps, ProfileFormValues, RegisteredAddressFieldsValues } from '@/pages/UserAccount/Profile';
import { FormHasDirtyFields } from '@/pages/UserAccount/Profile/ui/ProfilePresentational';

import { SelectOption } from '@/shared/types';
import { FormSelect, FormTextInput } from '@/shared/ui';

import { isCountryUkraine, isRegionKyiv, isRegionSevastopol } from '../model';

import BaseChapterTemplate from './BaseChapterTemplate/BaseChapterTemplate';

interface RegisteredAddressProps extends BaseChapterProps {
  onSubmit: (values: ProfileFormValues) => void;
  optionsCountry: SelectOption[];
  optionsRegion: SelectOption[];
  optionsDistrict: SelectOption[];
  optionsCity: SelectOption[];
  optionsStreet: SelectOption[];
  setFormHasDirtyFields: (value: FormHasDirtyFields) => void;
  activeKey: Array<NonNullable<FormHasDirtyFields>>;
}

const registeredAddressFields = [
  'apartmentOfReg',
  'buildingOfReg',
  'cityOfReg',
  'countryOfReg',
  'regionOfReg',
  'districtOfReg',
  'houseOfReg',
  'streetOfReg',
] satisfies Array<keyof RegisteredAddressFieldsValues>;

export const RegisteredAddress: FC<RegisteredAddressProps> = ({
  onSubmit,
  onReset,
  optionsCountry,
  optionsRegion,
  optionsDistrict,
  optionsCity,
  optionsStreet,
  setFormHasDirtyFields,
  activeKey,
}) => {
  const {
    control,
    watch,
    handleSubmit,
    formState: { dirtyFields },
  } = useFormContext<ProfileFormValues>();

  const countryOfReg = watch('countryOfReg');
  const regionOfReg = watch('regionOfReg');
  const districtOfReg = watch('districtOfReg');
  const cityOfReg = watch('cityOfReg');
  const streetOfReg = watch('streetOfReg');

  const isRegisteredAddressFieldsDirty = Object.keys(dirtyFields).some((key) =>
    registeredAddressFields.includes(key as keyof RegisteredAddressFieldsValues),
  );

  useEffect(() => {
    if (!isRegisteredAddressFieldsDirty) return setFormHasDirtyFields(null);

    setFormHasDirtyFields('registered');
  }, [isRegisteredAddressFieldsDirty]);

  useEffect(() => {
    if (!activeKey.includes('registered') && isRegisteredAddressFieldsDirty) onReset();
  }, [activeKey]);

  const isUkraine = isCountryUkraine(countryOfReg?.id ?? '');
  const isKyiv = isRegionKyiv(regionOfReg?.id ?? '');
  const isSevastopol = isRegionSevastopol(regionOfReg?.id ?? '');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <BaseChapterTemplate
        fields={[
          <FormSelect
            options={(optionsCountry ?? []) satisfies SelectOption[]}
            control={control}
            name="countryOfReg"
            label="Країна реєстрації"
            key="countryOfReg"
          />,
          isUkraine ? (
            <FormSelect
              options={(optionsRegion ?? []) satisfies SelectOption[]}
              control={control}
              name="regionOfReg"
              label="Область реєстрації"
              key="regionOfReg"
              disabled={!optionsRegion?.length || !countryOfReg}
            />
          ) : null,
          isUkraine ? (
            <FormSelect
              options={(optionsDistrict ?? []) satisfies SelectOption[]}
              control={control}
              name="districtOfReg"
              label="Район реєстрації"
              key="districtOfReg"
              disabled={!optionsDistrict?.length || !regionOfReg || isKyiv || isSevastopol}
            />
          ) : null,
          isUkraine ? (
            <FormSelect
              options={(isKyiv || isSevastopol ? optionsRegion : optionsCity) satisfies SelectOption[]}
              control={control}
              name="cityOfReg"
              label="Населений пункт реєстрації"
              key="cityOfReg"
              disabled={!optionsCity?.length || !districtOfReg || isKyiv || isSevastopol}
            />
          ) : (
            <FormTextInput
              control={control}
              name="cityOfReg"
              label="Населений пункт реєстрації"
              placeholder="Вкажіть населений пункт"
              key="cityOfReg"
              disabled={!countryOfReg}
            />
          ),
          isUkraine ? (
            <FormSelect
              options={(optionsStreet ?? []) satisfies SelectOption[]}
              control={control}
              name="streetOfReg"
              label="Вулиця реєстрації"
              key="streetOfReg"
              disabled={!optionsStreet?.length || !cityOfReg}
            />
          ) : (
            <FormTextInput
              control={control}
              name="streetOfReg"
              label="Вулиця реєстрації"
              placeholder="Вкажіть вулицю"
              key="streetOfReg"
              disabled={!countryOfReg}
            />
          ),
          <FormTextInput
            control={control}
            label="Номер будинку реєстрації"
            name="houseOfReg"
            placeholder="Вкажіть будинок"
            key="houseOfReg"
            disabled={!streetOfReg || (!isUkraine && !cityOfReg && !streetOfReg)}
          />,
          <FormTextInput
            control={control}
            name="buildingOfReg"
            label="Номер корпусу реєстрації"
            placeholder="Вкажіть корпус"
            key="buildingOfReg"
            disabled={!streetOfReg || (!isUkraine && !cityOfReg && !streetOfReg)}
          />,
          <FormTextInput
            control={control}
            name="apartmentOfReg"
            label="Номер квартири реєстрації"
            placeholder="Вкажіть номер квартири"
            key="apartmentOfReg"
            disabled={!streetOfReg || (!isUkraine && !cityOfReg && !streetOfReg)}
          />,
        ]}
        isDisabledSubmitAction={isRegisteredAddressFieldsDirty}
        onReset={onReset}
      />
    </form>
  );
};
